import { useEmbedContext } from '@thoughtspot/blink-context';
import { envFlags, FLAGS } from '@thoughtspot/env-flags';
import { useTranslation } from '@thoughtspot/i18n';
import { isUnsavedChangesModalEnabledForAdhocAnswer } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { useCreateAnswerSessionMutation } from '/@services/answer/answer-service';
import {
    BachSessionId,
    SageSearchResponse,
} from '/@services/generated/graphql-types';
import { IncomingEventType, SearchParams } from '/@utils/embed.util';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import { useRuntimeParams } from '/@utils/runtime-filters.util';
import { NavigationModalContainerProps } from '../components/navigation-blocker/navigation-blocker-modal.container';
import { isQueryEmptyWithoutSpaces } from '../components/sage-search-bar/sage-search-box/sage-search-box.util';
import { BaseAnswerLayoutContextValue } from './base-answer-layout-context';

export type AdhocAnswerLayoutContextValue = BaseAnswerLayoutContextValue;

const AdhocAnswerLayoutContext = createContext<AdhocAnswerLayoutContextValue | null>(
    null,
);
AdhocAnswerLayoutContext.displayName = 'AdhocAnswerLayoutContext';

const AdhocAnswerLayoutProviderInner: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { t } = useTranslation();
    const runtimeParams = useRuntimeParams();
    const {
        useEmbedEventSubscription,
        embedParams: { dataSources: dataSourcesFromEmbedParams },
    } = useEmbedContext();

    const [answerSessionId, setAnswerSessionId] = useState('');
    const [answerId, setAnswerId] = useState('');
    const [dataSourceIds, setDataSourceIds] = useState(
        dataSourcesFromEmbedParams || [],
    );
    const [sourceUpdatedByHostEvent, setSourceUpdatedByHostEvent] = useState(
        false,
    );

    // Persisted across the Answer<->Spreadsheet child-route remount (this
    // provider stays mounted). Lets the Answer view restore sage tokens and
    // skip auto-focus when returning from the Spreadsheet.
    const [
        cachedSageQueryState,
        setCachedSageQueryState,
    ] = useState<SageSearchResponse | null>(null);
    const [hasVisitedSpreadsheet, setHasVisitedSpreadsheet] = useState(false);
    const markSpreadsheetVisited = useCallback(
        () => setHasVisitedSpreadsheet(true),
        [],
    );

    const dataPaginationParams = {
        isClientPaginated: true,
        offset: 0,
        size: envFlags.getValue(FLAGS.dataBatchSize?.name) || 1000,
    };

    const [
        createSession,
        { data, loading, error },
    ] = useCreateAnswerSessionMutation(
        (session: BachSessionId, newAnswerId: string) => {
            setAnswerSessionId(session.sessionId);
            setAnswerId(newAnswerId);
        },
        dataSourceIds,
        !(dataSourcesFromEmbedParams || sourceUpdatedByHostEvent),
        dataPaginationParams,
    );

    const navModalProps:
        | NavigationModalContainerProps
        | false = isUnsavedChangesModalEnabledForAdhocAnswer() && {
        title: t('documentUnsavedChangesAlert.UNSAVED_CHANGES'),
        navServicePromptMessage: t('answer.unsavedChangesAlert.message'),
        confirmButton: {},
        denyButton: {},
        dismissButton: {},
    };

    // Handles the incoming search event to refresh the data sources.
    // If the payload includes new data sources but no search query,
    // it updates dataSourceIds and creates a new session.
    // If the search query is provided, it will be managed by
    // use-search-embed-events.ts.
    useEmbedEventSubscription(
        IncomingEventType.Search,
        (payload: SearchParams) => {
            const { searchQuery, dataSources } = payload || {};
            const isValidQuery =
                !searchQuery || isQueryEmptyWithoutSpaces(searchQuery);
            if (!_.isEmpty(dataSources) && isValidQuery) {
                setSourceUpdatedByHostEvent(true);
                // Clear session immediately so the loading gate
                // (!answerSessionId) activates before createSession() fires.
                setAnswerSessionId('');
                setDataSourceIds(dataSources);
            }
        },
    );

    useEffect(() => {
        createSession();
    }, [createSession, dataSourceIds, runtimeParams]);

    return (
        <AdhocAnswerLayoutContext.Provider
            value={{
                answerSessionId,
                answerId,
                data,
                loading,
                error,
                navModalProps,
                cachedSageQueryState,
                setCachedSageQueryState,
                hasVisitedSpreadsheet,
                markSpreadsheetVisited,
            }}
        >
            {children}
        </AdhocAnswerLayoutContext.Provider>
    );
};

export const AdhocAnswerLayoutProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    // In embedded app contexts (full-app embed), skip the layout provider so
    // AdhocAnswer falls back to standalone mode. Spreadsheet and the shared
    // session-creation logic are not supported in embedded contexts.
    if (isEmbeddedApp()) {
        return <>{children}</>;
    }
    return (
        <AdhocAnswerLayoutProviderInner>
            {children}
        </AdhocAnswerLayoutProviderInner>
    );
};

export const useAdhocAnswerLayoutContext = (): AdhocAnswerLayoutContextValue => {
    const ctx = useContext(AdhocAnswerLayoutContext);
    if (ctx === null) {
        throw new Error(
            'useAdhocAnswerLayoutContext must be used within AdhocAnswerLayoutProvider',
        );
    }
    return ctx;
};

/**
 * Returns the layout context when available, or null when AdhocAnswer is
 * rendered standalone (without AdhocAnswerLayoutProvider as an ancestor).
 *
 * Backward-compatibility shim — callers that do not yet wrap AdhocAnswer
 * in AdhocAnswerLayoutProvider will get null here and fall back to their
 * own loading logic.
 *
 * TODO: once every callsite is migrated to use AdhocAnswerLayoutProvider,
 * remove this hook and replace usages with useAdhocAnswerLayoutContext.
 */
export const useOptionalAdhocAnswerLayoutContext = (): AdhocAnswerLayoutContextValue | null =>
    useContext(AdhocAnswerLayoutContext);
